import { type MondayClientSdk } from 'monday-sdk-js/types/client-sdk.interface';
import * as _ from 'lodash';

type GraphqlVariables = Record<string, any>;

type ErrorType = 'unknown' | 'empty' | 'runtime' | 'client' | 'server';

type MondayRequesterErrorResult = {
  type: 'error';

  errorType: ErrorType;
  errorText: string;
  errors?: any[];

  statusCode?: number;
  scope: string;

  graphqlQuery: string;
  graphqlVariables?: GraphqlVariables;
};

type MondayRequesterSuccessResult = {
  type: 'success';
  scope: string;
  graphqlQuery: string;
  graphqlVariables?: GraphqlVariables;
  data: any;
};

type MondayRequesterResult = MondayRequesterSuccessResult | MondayRequesterErrorResult;

export function extractErrorText(err: any) {
  let errorText;
  let errors = null;

  if (_.isString(_.get(err, 'errors.0.message'))) {
    errors = _.get(err, 'errors');
  }

  if (Array.isArray(errors)) {
    errorText = errors
      .map((error: any) => error.message)
      .filter((message: any) => _.isString(message))
      .join(', ');
  } else if (typeof err === 'string') {
    errorText = err;
  } else if (err instanceof Error) {
    errorText = err.message;
  } else if (typeof err === 'object' && err && 'message' in err && typeof err.message === 'string') {
    errorText = err.message;
  } else if (err?.toString) {
    errorText = err.toString();
  } else if (err) {
    try {
      errorText = JSON.stringify(err);
    } catch (e) {}
  }

  if (!errorText) {
    errorText = 'Unknown error';
  }

  return errorText;
}

/*
 EXAMPLE ERROR (API VERSION 2025-07):

 STATUS: 200
  {
      "data": {
          "complexity": {
              "query": 4078
          },
          "boards": [
              null
          ]
      },
      "errors": [
          {
              "message": "Column not found",
              "locations": [
                  {
                      "line": 1,
                      "column": 104
                  }
              ],
              "path": [
                  "boards",
                  0,
                  "items_page"
              ],
              "extensions": {
                  "code": "ResourceNotFoundException",
                  "status_code": 404,
                  "error_data": {
                      "resource_type": "column",
                      "column_id": "status",
                      "board_id": 1963119331,
                      "error_reason": "store.monday.automation.error.missing_column"
                  }
              }
          }
      ],
      "extensions": {
          "request_id": "50c0fbf5-b5ec-9588-9c72-ee1acb72be15"
      }
  }


  STATUS: 400

  {
    "errors": [
        {
            "message": "Variable \"$limit\" is never used.",
            "locations": [
                {
                    "line": 2,
                    "column": 45
                }
            ],
            "extensions": {
                "code": "GRAPHQL_VALIDATION_FAILED"
            }
        },
        {
            "message": "Field \"boards\" of type \"Query\" must have a selection of subfields. Did you mean \"boards { ... }\"?",
            "locations": [
                {
                    "line": 6,
                    "column": 7
                }
            ],
            "extensions": {
                "code": "GRAPHQL_VALIDATION_FAILED"
            }
        },
        {
            "message": "Cannot query field \"items_page_asdas\" on type \"Board\".",
            "locations": [
                {
                    "line": 7,
                    "column": 9
                }
            ],
            "extensions": {
                "code": "GRAPHQL_VALIDATION_FAILED"
            }
        }
    ],
    "account_id": 20438527
}

  STATUS: 500

  {\"errors\":[{\"message\":\"Internal server error\",\"path\":[],\"extensions\":{\"status_code\":500,\"error_code\":\"INTERNAL_SERVER_ERROR\"}}],\"extensions\":{\"request_id\":\"64015243-b05b-915b-a8b1-92b1d789631f\"},\"status_code\":500,\"error_message\":\"Internal server error\",\"account_id\":17155708}
*/

/*
  Tried to calculate too many formulas in a short period of time:

  {
    "data": {
        "complexity": {
            "query": 3078
        },
        "boards": [
            null
        ]
    },
    "errors": [
        {
            "message": "Rate limit exceeded for the field.",
            "path": [
                "boards",
                0,
                "items_page",
                "items",
                0,
                "column_values",
                7,
                "display_value"
            ],
            "extensions": {
                "field": "display_value",
                "retry_in_seconds": 25,
                "status_code": 429,
                "code": "FIELD_MINUTE_RATE_LIMIT_EXCEEDED",
                "service": "query-api"
            }
        }
    ],
    "extensions": {
        "request_id": "9a0a8fe0-db1b-9799-b3f8-bdd03f6d22c0"
    }
}
*/

function graphqlErrorToHuman(error: any, variables?: GraphqlVariables) {
  // column not found
  if (
    _.get(error, 'extensions.code') === 'ResourceNotFoundException' &&
    _.get(error, 'extensions.error_data.error_reason') === 'store.monday.automation.error.missing_column'
  ) {
    const columnId = _.get(error, 'extensions.error_data.column_id');
    const boardId = _.get(error, 'extensions.error_data.board_id');

    return `There is a board column used in the configuration which does not seem to exist anymore. It's the column "${columnId}" from the board "${boardId}". Please fix this by removing the column from the configuration.`;
  }

  // invalid rules
  if ((_.get(error, 'message') || '').includes('invalid') && (_.get(error, 'message') || '').includes('rules')) {
    return `Some of the filter rules you are trying to use are invalid. Please check the filter rules and make sure they are valid.`;
  }

  const isBoardRelatedGraphqlQuery = _.has(variables, 'boardId') || _.has(variables, 'boardIds') || _.has(variables, 'cursor');

  // internal server error
  if (_.get(error, 'message') === 'Internal server error' || _.get(error, 'extensions.error_code') === 'INTERNAL_SERVER_ERROR') {
    if (isBoardRelatedGraphqlQuery) {
      return `The monday.com API is experiencing issues with the board you are trying to access. This is most likely a temporary issue. Please try again later. If the issue persists, try running the "Diagnose" tool to check for any issues with the board. Sometimes the usage of mirror columns can cause unexpected issues or one faulty item can cause the entire board to fail.`;
    }

    return `The monday.com API is experiencing issues. This is most likely a temporary issue. Please try again later.`;
  }

  // API_TEMPORARILY_BLOCKED
  if (_.get(error, 'extensions.error_code') === 'API_TEMPORARILY_BLOCKED') {
    return `The monday.com API is experiencing some temporary issues. Please try again later.`;
  }

  // too many formulas
  if (_.get(error, 'extensions.code') === 'FIELD_MINUTE_RATE_LIMIT_EXCEEDED') {
    return `The monday.com API rate limit for formula calculations has been exceeded. Monday only allows up to 10.000 formula calculations per minute.`;
  }

  return null;
}

function extractHumanErrors(err: any, variables?: GraphqlVariables) {
  let errors = null;

  if (_.isString(_.get(err, 'errors.0.message'))) {
    errors = _.get(err, 'errors');
  }

  return (errors || []).map((error: any) => graphqlErrorToHuman(error, variables)).filter((error: any) => error);
}

function extractRequestId(err: any) {
  if (_.isString(_.get(err, 'extensions.request_id'))) {
    return _.get(err, 'extensions.request_id');
  }

  return null;
}

function extractErrorFieldsFromJsonErrorResponse(jsonErrorResult: any, errorType: ErrorType, variables?: GraphqlVariables) {
  return {
    requestId: extractRequestId(jsonErrorResult),
    errorType,
    errorText: extractErrorText(jsonErrorResult),
    errors: _.get(jsonErrorResult, 'errors'),
    humanErrors: extractHumanErrors(jsonErrorResult, variables),
  };
}

export class MondayRequesterError extends Error {
  requesterErrorResult: MondayRequesterErrorResult;

  constructor(requesterErrorResult: MondayRequesterErrorResult) {
    super(`${requesterErrorResult.errorType} error: ${requesterErrorResult.errorText}`);

    this.name = 'MondayRequesterError';
    this.requesterErrorResult = requesterErrorResult;

    this.stack = new Error().stack;
  }
}

export class AbortedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AbortedError';

    this.stack = new Error().stack;
  }
}

type MondayRequester = (
  scope: string,
  query: string,
  variables?: GraphqlVariables,
  abortSignal?: AbortSignal,
  options?: { isFileUpload?: boolean; apiVersion?: string },
) => Promise<MondayRequesterResult>;

function createRateLimitAwareRequester(requester: MondayRequester): MondayRequester {
  return async (scope, query, variables, abortSignal, options) => {
    const result = await requester(scope, query, variables, abortSignal, options);

    if (result.type === 'error') {
      let isComplexityError = false;

      if (result.errorText === 'Complexity budget exhausted') {
        isComplexityError = true;
      } else if (Array.isArray(result.errors) && result.errors.length > 0) {
        isComplexityError = result.errors.some(
          (error) =>
            error?.extensions?.code === 'COMPLEXITY_BUDGET_EXHAUSTED' || error?.extensions?.code === 'FIELD_MINUTE_RATE_LIMIT_EXCEEDED',
        );
      }

      if (isComplexityError) {
        const waitTime = _.get(result, 'errors.0.extensions.retry_in_seconds') || 55;
        console.log(`Request failed due to complexity budget exhaustion. Waiting for ${waitTime + 5} seconds before retrying...`);

        // wait for the specified time before retrying
        await new Promise((resolve) => setTimeout(resolve, (waitTime + 5) * 1000));

        if (abortSignal?.aborted) {
          throw new AbortedError('Request has been aborted while waiting to retry after complexity budget exhaustion');
        }

        // retry the request once after waiting
        return requester(scope, query, variables, abortSignal, options);
      }
    }

    return result;
  };
}

export function createMondayFetchRequester(authToken: string, apiVersion = '2025-07'): MondayRequester {
  return async (scope, query, variables, abortSignal, options) => {
    try {
      let response;

      if (options?.isFileUpload) {
        const formData = new FormData();
        let variablesWithoutFile: Record<string, any> = {};
        let variablesWithFile: Record<string, any> = {};

        formData.append('query', query);

        if (variables) {
          for (const key in variables) {
            if (variables[key] instanceof File) {
              variablesWithFile[key] = variables[key];
            } else {
              variablesWithoutFile[key] = variables[key];
            }
          }
        }

        if (Object.keys(variablesWithoutFile).length > 0) {
          formData.append('variables', JSON.stringify(variablesWithoutFile));
        }

        const mapField: Record<string, string> = {};

        if (Object.keys(variablesWithFile).length > 0) {
          for (const key in variablesWithFile) {
            mapField[key] = `variables.${key}`;
            formData.append(key, variablesWithFile[key]);
          }
        }

        formData.append('map', JSON.stringify(mapField));

        response = await fetch('https://api.monday.com/v2/file', {
          method: 'POST',
          signal: abortSignal,
          headers: {
            Authorization: authToken,
            ...((options?.apiVersion ?? apiVersion) ? { 'API-Version': options?.apiVersion ?? apiVersion } : {}),
            // Content-Type is automatically set by the browser or Node.js when using FormData
          },
          body: formData,
        });
      } else {
        response = await fetch('https://api.monday.com/v2', {
          method: 'POST',
          signal: abortSignal,
          headers: {
            'Content-Type': 'application/json',
            Authorization: authToken,
            ...((options?.apiVersion ?? apiVersion) ? { 'API-Version': options?.apiVersion ?? apiVersion } : {}),
          },
          body: JSON.stringify({
            query,
            variables,
          }),
        });
      }

      // the response is a success if:
      // - response is ok (2xx)
      // - response payload can be parsed as JSON
      // - there are no errors in the response payload

      let jsonResult;
      let textResult;

      try {
        jsonResult = await response.clone().json();
      } catch (err) {
        textResult = await response.clone().text();
        // if the response is not ok, we can still parse it as JSON
        // but we don't need to do that here
      }

      if (response.ok && jsonResult && !_.get(jsonResult, 'errors')) {
        return {
          type: 'success',
          data: _.get(jsonResult, 'data'),
          graphqlQuery: query,
          graphqlVariables: variables,
          scope,
        };
      }

      let errorType: ErrorType = 'runtime';

      if (response.status >= 400 && response.status < 500) {
        errorType = 'client';
      }

      if (response.status >= 500) {
        errorType = 'server';
      }

      if (jsonResult) {
        return {
          type: 'error',
          statusCode: response.status,
          graphqlQuery: query,
          graphqlVariables: variables,
          scope,
          ...extractErrorFieldsFromJsonErrorResponse(jsonResult, errorType, variables),
        };
      }

      return {
        type: 'error',
        statusCode: response.status,
        graphqlQuery: query,
        graphqlVariables: variables,
        scope,
        errorType,
        errorText: textResult ? textResult : 'Unknown error',
      };
    } catch (err) {
      return {
        type: 'error',
        errorType: 'unknown',
        errorText: extractErrorText(err),
        graphqlQuery: query,
        graphqlVariables: variables,
        scope,
      };
    }
  };
}

export function createMondaySdkRequester(monday: MondayClientSdk): MondayRequester {
  return async (scope, query, variables) => {
    try {
      const result = await monday.api(query, { variables });

      return {
        type: 'success',
        data: result.data,
        graphqlQuery: query,
        graphqlVariables: variables,
        scope,
      };
    } catch (err) {
      if (Array.isArray(_.get(err, 'data.errors'))) {
        return {
          type: 'error',
          graphqlQuery: query,
          graphqlVariables: variables,
          scope,
          ...extractErrorFieldsFromJsonErrorResponse(_.get(err, 'data'), 'runtime', variables),
        };
      } else {
        return {
          type: 'error',
          errorType: 'unknown',
          errorText: extractErrorText(err),
          graphqlQuery: query,
          graphqlVariables: variables,
          scope,
        };
      }
    }
  };
}

type QueryHistoryEntry = {
  query: string;
  variables?: GraphqlVariables;
  requestStart: number;
  requestEnd: number;
  result: MondayRequesterResult;
  scope: string;
  complexity?: number;
};

type MondayClientOptions = {
  queryHistorySize?: number;
  errorQueryHistorySize?: number;
};

const mondayClientDefaultOptions: MondayClientOptions = {
  queryHistorySize: 20,
  errorQueryHistorySize: 20,
};

export function createMondayClient(requester: MondayRequester, options: MondayClientOptions = {}) {
  const { queryHistorySize } = { ...mondayClientDefaultOptions, ...options };
  const wrappedRequester = createRateLimitAwareRequester(requester);

  let queryHistory: QueryHistoryEntry[] = [];
  let errorQueryHistory: QueryHistoryEntry[] = [];

  const api = {
    async query(
      query: string,
      variables?: GraphqlVariables,
      abortSignal?: AbortSignal,
      optionsOrScope: string | { scope?: string; isFileUpload?: boolean; apiVersion?: string } = 'DEFAULT',
    ) {
      const scope = typeof optionsOrScope === 'string' ? optionsOrScope : optionsOrScope.scope || 'DEFAULT';
      const isFileUpload = !!(typeof optionsOrScope === 'object' && optionsOrScope.isFileUpload);
      const apiVersion = typeof optionsOrScope === 'object' ? optionsOrScope.apiVersion : undefined;
      const requestStart = Date.now();
      const result = await wrappedRequester(scope, query, variables, abortSignal, { isFileUpload, apiVersion });
      const requestEnd = Date.now();
      let complexity;

      if (result.type === 'success') {
        complexity = result.data?.complexity?.query as number | undefined;
      }

      const historyEntry: QueryHistoryEntry = {
        query,
        variables,
        requestStart,
        requestEnd,
        result,
        scope,
        complexity,
      };

      queryHistory.unshift(historyEntry);
      queryHistory = queryHistory.slice(0, queryHistorySize);

      if (result.type === 'error') {
        errorQueryHistory.unshift(historyEntry);
        errorQueryHistory = errorQueryHistory.slice(0, options.errorQueryHistorySize);
      }

      return result;
    },

    getQueryHistory() {
      return queryHistory;
    },

    getErrorQueryHistory() {
      return errorQueryHistory;
    },

    scopedClient(scope: string) {
      return {
        ...api,

        query(query: string, variables?: GraphqlVariables, abortSignal?: AbortSignal) {
          return api.query(query, variables, abortSignal, scope);
        },
      };
    },
  };

  return api;
}

export type MondayClient = ReturnType<typeof createMondayClient>;

export function extractApiResult<T>(result: MondayRequesterSuccessResult, resultPath: string, dataMapper?: (arg: T) => T): T {
  const res = _.get(result.data, resultPath) as T;

  if (res === undefined) {
    const requesterErrorResult: MondayRequesterErrorResult = {
      type: 'error',
      errorType: 'empty',
      errorText: `Could not find result at path ${resultPath}`,
      graphqlQuery: result.graphqlQuery,
      graphqlVariables: result.graphqlVariables,
      scope: result.scope,
    };

    throw new MondayRequesterError(requesterErrorResult);
  }

  if (dataMapper) {
    return dataMapper(res);
  }

  return res;
}

export async function getAllResults<T>(
  fetcherFn: (page: number, limit: number) => Promise<T[]>,
  perPage: number,
  abortSignal?: AbortSignal,
): Promise<T[]> {
  let page = 0;
  let allResults: T[] = [];
  let resultsLen;

  do {
    const results = await fetcherFn(page, perPage);
    resultsLen = results.length;
    allResults.push(...results);
    page++;
  } while (resultsLen === perPage && !abortSignal?.aborted);

  if (abortSignal?.aborted) {
    throw new AbortedError('Aborted loading');
  }

  return allResults;
}
